import { billingService } from './billing';
import { redirectToExternal } from '../utils/pwa';
import {
  clearCheckoutState,
  clearPendingCheckout,
  markRedirectedToStripe,
  setCheckoutBillingType,
  setCheckoutPurchaseEventId,
  setPendingCheckout,
  CheckoutType,
} from '../utils/checkoutStorage';
import {
  createMetaEventId,
  getMetaCookies,
  getMetaEventSourceUrl,
  hasStoredMarketingConsent,
} from '../utils/metaTracking';
import { getOpenAIAttributionCookies } from '../utils/openaiPixel';
import type { CheckoutMetaContext } from './metaEvents';

// Stripe price IDs (from environment variables)
const stripePriceMonthly = process.env.REACT_APP_STRIPE_PRICE_MONTHLY;
const stripePriceYearly = process.env.REACT_APP_STRIPE_PRICE_YEARLY;

if (!stripePriceMonthly || !stripePriceYearly) {
  if (process.env.NODE_ENV !== 'production') {
    console.warn('REACT_APP_STRIPE_PRICE_MONTHLY or REACT_APP_STRIPE_PRICE_YEARLY is not set. Stripe pricing will not work.');
  }
}

// Exported Stripe price IDs
export const STRIPE_PRICE_IDS = {
  monthly: stripePriceMonthly || '', // 18€/month
  yearly: stripePriceYearly || '',   // 180€/year
} as const;

/**
 * Standard SaaS:
 * - The frontend never talks to Stripe directly.
 * - It asks the backend to create a Checkout Session and redirects to the returned URL.
 * 
 * Note: In PWA standalone mode, window.location.href works correctly for Stripe
 * because Stripe handles the return_url redirect back to the app.
 * The return_url must be configured in the backend to use the full URL (not relative).
 */
export const redirectToCheckout = async (priceId: string, type: CheckoutType): Promise<void> => {
  try {
    const marketingConsent = hasStoredMarketingConsent();
    const purchaseEventId = createMetaEventId();
    setCheckoutPurchaseEventId(purchaseEventId);

    const meta: CheckoutMetaContext | undefined = marketingConsent
      ? {
          purchaseEventId,
          billingType: type,
          marketingConsent: true,
          eventSourceUrl: getMetaEventSourceUrl(),
          ...getMetaCookies(),
          ...getOpenAIAttributionCookies(),
        }
      : undefined;

    const { url } = await billingService.createCheckout(priceId, meta);

    // Redirect to Stripe-hosted Checkout
    // Using redirectToExternal ensures it works in both browser and PWA standalone mode
    redirectToExternal(url, {
      // Don't open in new window - Stripe needs to redirect back to return_url
      openInNewWindow: false,
      // If navigation is interrupted (ex: user hits back quickly), treat as "not completed" (cancelled flow),
      // not a technical payment error.
      fallbackUrl: '/pricing?checkout=cancelled'
    });
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

/**
 * Standard SaaS checkout flow (shared):
 * - Store intent (priceId/type) so auth flows can pick it up if needed
 * - Mark redirected flag for back-button detection
 * - Redirect to Stripe Checkout URL
 * - Clear intent after redirect is initiated (keep redirected flag)
 */
export const startCheckoutRedirect = async (priceId: string, type: CheckoutType): Promise<void> => {
  try {
    setPendingCheckout(priceId, type);
    setCheckoutBillingType(type);
    markRedirectedToStripe();
    await redirectToCheckout(priceId, type);
    // If redirect was initiated, prevent retrigger loops.
    clearPendingCheckout();
  } catch (error) {
    // On any error, clear everything to avoid stale flags
    clearCheckoutState();
    throw error;
  }
};

